import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  KeyRoundIcon,
  Loader2Icon,
  PlusIcon,
  PowerIcon,
  RotateCcwIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import { toast } from "sonner";

import { ErrorState, FieldError } from "@/components/error-state";
import { SecretRevealDialog } from "@/components/secret-reveal-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminService, type Account } from "@/gen/wabms/admin/v1/admin_pb";
import { invalidateAccounts } from "@/lib/admin-queries";
import { errorMessage, unclaimedViolations, violationFor } from "@/lib/errors";
import { formatBoth, formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Field paths this form owns, matched against ErrorDetail.field_violations. */
const WEBHOOK_FIELDS = ["default_url", "allowed_url_prefixes", "reenable"] as const;

export function WebhooksTab({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const config = account.webhook;
  const initial = useMemo(
    () => ({
      defaultUrl: config?.defaultUrl ?? "",
      prefixes: config?.allowedUrlPrefixes ?? [],
    }),
    [config],
  );

  const [defaultUrl, setDefaultUrl] = useState(initial.defaultUrl);
  const [prefixes, setPrefixes] = useState<string[]>(initial.prefixes);
  const [newPrefix, setNewPrefix] = useState("");
  const [reenable, setReenable] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  /**
   * RotateWebhookSecret returns the plaintext exactly once. It lives in this
   * state and nowhere else, is cleared when the reveal dialog closes, and the
   * mutation is reset straight after the response is read so the value is not
   * retained in the mutation cache either.
   */
  const [rotatedSecret, setRotatedSecret] = useState<string | undefined>(undefined);

  const updateConfig = useMutation(AdminService.method.updateWebhookConfig);
  const rotateSecret = useMutation(AdminService.method.rotateWebhookSecret);

  // Rebase the form whenever the account is refetched.
  useEffect(() => {
    setDefaultUrl(initial.defaultUrl);
    setPrefixes(initial.prefixes);
    setReenable(false);
  }, [initial]);

  const disabledAt = config?.disabledAt;
  const failures = config?.consecutiveFailures ?? 0;
  const dirty =
    defaultUrl !== initial.defaultUrl ||
    prefixes.join(" ") !== initial.prefixes.join(" ") ||
    reenable;

  const urlValid = defaultUrl === "" || /^https:\/\/.+/.test(defaultUrl);
  const urlCovered =
    defaultUrl === "" || prefixes.length === 0 || prefixes.some((p) => defaultUrl.startsWith(p));

  const urlViolation = violationFor(error, "default_url");
  const prefixViolation = violationFor(error, "allowed_url_prefixes");
  const otherViolations = unclaimedViolations(error, WEBHOOK_FIELDS);

  function addPrefix() {
    const value = newPrefix.trim();
    if (value === "" || prefixes.includes(value)) return;
    setPrefixes((p) => [...p, value]);
    setNewPrefix("");
  }

  async function save() {
    setError(undefined);
    try {
      await updateConfig.mutateAsync({
        accountId: account.accountId,
        defaultUrl,
        allowedUrlPrefixes: prefixes,
        reenable,
      });
      setReenable(false);
      await invalidateAccounts(queryClient);
      toast.success("Webhook configuration saved");
    } catch (err) {
      setError(err);
      toast.error("Webhook configuration not saved", { description: errorMessage(err) });
    }
  }

  async function rotate() {
    try {
      const res = await rotateSecret.mutateAsync({ accountId: account.accountId });
      const secret = res.secret;
      rotateSecret.reset();
      setRotatedSecret(secret);
      // `secret_set` flips on the account, so the summary line has to refresh.
      await invalidateAccounts(queryClient);
    } catch (err) {
      toast.error("Could not rotate the signing secret", { description: errorMessage(err) });
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      {disabledAt ? (
        <Alert variant="destructive" className="border-tone-danger/50 bg-tone-danger/5">
          <PowerIcon />
          <AlertTitle>Webhook endpoint auto-disabled</AlertTitle>
          <AlertDescription>
            <p>
              The service stopped delivering to this endpoint {formatBoth(disabledAt)} after{" "}
              {formatCount(failures)} consecutive failures. Events are still recorded, but nothing
              is being pushed.
            </p>
            <p className="mt-1">
              Fix the endpoint first, then tick “re-enable” below and save. Re-enabling clears the
              failure counter; it does not replay the events that were dropped.
            </p>
            <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-current"
                checked={reenable}
                onChange={(e) => setReenable(e.target.checked)}
              />
              Re-enable delivery on save
            </label>
          </AlertDescription>
        </Alert>
      ) : failures > 0 ? (
        <Alert className="border-tone-warning/40 bg-tone-warning/5">
          <TriangleAlertIcon className="text-tone-warning" />
          <AlertTitle>{formatCount(failures)} consecutive delivery failures</AlertTitle>
          <AlertDescription>
            The endpoint is still enabled, but it will be disabled automatically if the failures
            continue. Check the delivery log on the Webhooks screen.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Default URL</CardTitle>
          <CardDescription>
            Where events go when a CLIENT does not supply its own callback URL on submit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="default-url" className="sr-only">
            Default webhook URL
          </Label>
          <Input
            id="default-url"
            placeholder="https://ops.example.com/hooks/bmi"
            value={defaultUrl}
            aria-invalid={!urlValid || urlViolation !== undefined}
            onChange={(e) => setDefaultUrl(e.target.value)}
          />
          {!urlValid ? (
            <p className="text-xs text-destructive">The URL must be absolute and use https.</p>
          ) : urlViolation ? (
            <FieldError message={urlViolation} />
          ) : !urlCovered ? (
            <p className="text-xs text-tone-warning">
              This URL does not start with any allowed prefix, so deliveries to it would be
              refused.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Leave empty to require every CLIENT to pass a callback URL explicitly.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allowed URL prefixes</CardTitle>
          <CardDescription>
            A CLIENT-supplied callback URL is accepted only if it starts with one of these. With no
            prefixes the service accepts any https URL, which makes this account a usable
            SSRF relay — keep the list tight.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {prefixes.length === 0 ? (
            <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
              No prefixes configured.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {prefixes.map((prefix) => (
                <li
                  key={prefix}
                  className="flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5"
                >
                  <code className="font-mono text-xs break-all">{prefix}</code>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove ${prefix}`}
                    onClick={() => setPrefixes((p) => p.filter((x) => x !== prefix))}
                  >
                    <XIcon />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="https://ops.example.com/hooks/"
              value={newPrefix}
              onChange={(e) => setNewPrefix(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addPrefix();
                }
              }}
            />
            <Button variant="outline" onClick={addPrefix} disabled={newPrefix.trim() === ""}>
              <PlusIcon /> Add
            </Button>
          </div>
          <FieldError message={prefixViolation} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRoundIcon className="size-4" /> Signing secret
          </CardTitle>
          <CardDescription>
            Every delivery is signed with this secret so the receiver can verify it came from this
            service.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            {config?.secretSet ? (
              <span className="text-tone-success">A secret is configured.</span>
            ) : (
              <span className="text-tone-warning">
                No secret configured — deliveries are unsigned.
              </span>
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={rotateSecret.isPending}
            onClick={() => void rotate()}
          >
            {rotateSecret.isPending ? (
              <Loader2Icon className="animate-spin" />
            ) : (
              <RotateCcwIcon />
            )}
            {config?.secretSet ? "Rotate secret" : "Generate secret"}
          </Button>
        </CardContent>
      </Card>

      {error !== undefined &&
      (otherViolations.length > 0 ||
        (urlViolation === undefined && prefixViolation === undefined)) ? (
        <ErrorState error={error} title="Webhook configuration not saved" />
      ) : null}

      <div className="flex items-center gap-2">
        <Button disabled={!dirty || updateConfig.isPending || !urlValid} onClick={() => void save()}>
          {updateConfig.isPending ? <Loader2Icon className="animate-spin" /> : null}
          Save webhook configuration
        </Button>
        <Button
          variant="ghost"
          disabled={!dirty || updateConfig.isPending}
          onClick={() => {
            setDefaultUrl(initial.defaultUrl);
            setPrefixes(initial.prefixes);
            setReenable(false);
            setError(undefined);
          }}
        >
          Reset
        </Button>
        <span className={cn("text-xs text-muted-foreground", !dirty && "invisible")}>
          Unsaved changes
        </span>
      </div>

      <SecretRevealDialog
        open={rotatedSecret !== undefined}
        onOpenChange={(open) => !open && setRotatedSecret(undefined)}
        title="Webhook secret rotated"
        description="Update the receiving endpoint before the next event fires. Deliveries signed with the old secret will start failing verification immediately."
        secret={rotatedSecret ?? ""}
      />
    </div>
  );
}
